/**
 * Quick Result Modal — manual override for matches that cannot finish naturally.
 *
 * Outcomes: walkover, win_by_default, early_termination, abandoned, no_result, tie.
 * Backend: POST /api/v1/events/{eid}/live/{fid}/set-final-result
 */
import { useMemo, useState } from 'react';

const OUTCOMES = [
  { value: 'walkover',           label: 'Walkover',           hint: 'One team did not show up — pick the winner.' },
  { value: 'win_by_default',     label: 'Win by Default',     hint: 'Forfeit or penalty — pick the winner.' },
  { value: 'early_termination',  label: 'Early Termination',  hint: 'Match called early (DLS / weather). Reason required.' },
  { value: 'abandoned',          label: 'Abandoned',          hint: 'Could not be played — no winner.' },
  { value: 'no_result',          label: 'No Result',          hint: 'Match attempted but no outcome — no winner.' },
  { value: 'tie',                label: 'Tie',                hint: 'Manually declared tie — no winner.' },
];

const WINNER_OUTCOMES = new Set(['walkover', 'win_by_default', 'early_termination']);
const REASON_REQUIRED = new Set(['early_termination']);

export default function QuickResultModal({ fixture, onSubmit, onClose }) {
  const [outcome, setOutcome] = useState('walkover');
  const [winnerTeamId, setWinnerTeamId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selected = useMemo(() => OUTCOMES.find(o => o.value === outcome), [outcome]);
  const needsWinner = WINNER_OUTCOMES.has(outcome);
  const needsReason = REASON_REQUIRED.has(outcome);
  const canSubmit =
    !!outcome &&
    (!needsWinner || !!winnerTeamId) &&
    (!needsReason || !!reason.trim()) &&
    !submitting;

  const isLive = fixture.match_status === 'live';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        outcome,
        winning_team_id: needsWinner ? winnerTeamId : null,
        reason: reason.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="quick-result-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-result-modal"
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg text-[#2B3A67]">Quick Result</h3>
            <p className="text-xs text-gray-500 mt-1">
              Match #{fixture.match_number}: {fixture.team_a_name || 'TBD'} vs {fixture.team_b_name || 'TBD'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
            data-testid="quick-result-close"
          >×</button>
        </div>

        {isLive && (
          <div className="px-6 pt-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="quick-result-live-warning">
              <i className="ph-bold ph-warning-circle text-base flex-shrink-0 mt-0.5"></i>
              <span>
                This match is <strong>live</strong>. Setting a Quick Result will close it immediately.
                Existing innings &amp; ball-by-ball data are preserved.
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Outcome picker */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Outcome</label>
            <div className="grid grid-cols-2 gap-2" data-testid="quick-result-outcomes">
              {OUTCOMES.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { setOutcome(o.value); if (!WINNER_OUTCOMES.has(o.value)) setWinnerTeamId(''); }}
                  className={`text-left px-3 py-2 rounded-lg border-2 transition-colors text-xs font-medium ${
                    outcome === o.value
                      ? 'border-[#3AAFA9] bg-[#3AAFA9]/10 text-[#2B3A67]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid={`quick-result-outcome-${o.value}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {selected && (
              <p className="text-xs text-gray-500 mt-2" data-testid="quick-result-hint">{selected.hint}</p>
            )}
          </div>

          {/* Winner picker */}
          {needsWinner && (
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Winner <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setWinnerTeamId(fixture.team_a_id)}
                  className={`p-3 rounded-xl border-2 transition-all text-sm font-medium ${
                    winnerTeamId === fixture.team_a_id
                      ? 'border-[#3AAFA9] bg-[#3AAFA9]/10 text-[#2B3A67]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid="quick-result-winner-team-a"
                >
                  {fixture.team_a_name || 'Team A'}
                </button>
                <button
                  type="button"
                  onClick={() => setWinnerTeamId(fixture.team_b_id)}
                  className={`p-3 rounded-xl border-2 transition-all text-sm font-medium ${
                    winnerTeamId === fixture.team_b_id
                      ? 'border-[#3AAFA9] bg-[#3AAFA9]/10 text-[#2B3A67]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                  data-testid="quick-result-winner-team-b"
                >
                  {fixture.team_b_name || 'Team B'}
                </button>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Reason {needsReason ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(optional)</span>}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={needsReason ? 'e.g., Rain stopped play after 12 overs, DLS applied' : 'Optional context shown in the result summary'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#3AAFA9] focus:outline-none resize-none"
              data-testid="quick-result-reason"
              maxLength={500}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50"
              data-testid="quick-result-cancel"
            >Cancel</button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 py-2.5 bg-[#E54562] text-white font-bold rounded-xl hover:bg-[#d13a55] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="quick-result-submit"
            >
              {submitting ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <i className="ph-bold ph-flag-checkered"></i>
                  Apply Quick Result
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
